import { getPlatformEntry, parseRespData, PLATFORMS } from './mediaOptimizationConsts';

/**
 * Maps a raw /media-model/fetch (or /media-model/fetch-by-media) item into the
 * flat row model the media table consumes.
 *
 * Branches on the analysis shape:
 *  - `analysis.coreAnalysis` present → full v2 mapping (audience score, viral
 *    label, per-platform ratings).
 *  - otherwise → v1 graceful degrade (score from radScore only).
 * Non-completed items (queued/processing/failed) return a minimal row.
 *
 * Every row also carries `fileName` (the item's `path`) and `mediaURL` (the
 * original `imageURL`) so the library search can match on them.
 */

/**
 * Normalize the backend `processingStatus` into our lifecycle status.
 * - /media-model/fetch returns `processingStatus` ('processing'|'completed'|'failed';
 *   null/'pending' before the worker starts → treated as 'queued').
 * - /media-model/fetch-by-media omits `processingStatus`, so completion is inferred
 *   from `respData` being populated.
 */
function normalizeStatus(processingStatus, respData) {
  if (processingStatus === 'completed') return 'completed';
  if (processingStatus === 'failed') return 'failed';
  if (processingStatus === 'processing') return 'processing';
  // null / undefined / 'pending' / 'queued' — infer done if analysis is present.
  if (respData) return 'completed';
  return 'queued';
}

// platform_performance: { instagram: { rating: 'HIGH', rationale }, ... } → { instagram: 'HIGH', ... }
// Rating casing varies by analysis path (image vs. video), so the raw string is
// kept as-is here; consumers normalize case via getRatingInfo().
function extractRatings(platformPerformance) {
  const ratings = {};
  PLATFORMS.forEach(({ key }) => {
    const entry = getPlatformEntry(platformPerformance, key);
    if (entry?.rating) {
      ratings[key] = entry.rating;
    }
  });
  return ratings;
}

export function parseMediaItem(item = {}) {
  const respData = parseRespData(item.respData);

  // Audience name + subjectUrl are NOT on the media item — they're resolved by
  // joining `audienceId` (the fetch item's `personaRequestId`) + `segmentId`
  // against the /audiences list in Redux (see enrichMediaRows below). We keep
  // the join keys here and a best-effort fallback name from `personaName`.
  const hasPersonaName = item.personaName && item.personaName !== 'unassigned';

  const base = {
    id: item.id,
    status: normalizeStatus(item.processingStatus, respData),
    name: hasPersonaName ? item.personaName : 'Universal Audience',
    audienceId: item.personaRequestId ?? null,
    segmentId: item.segmentId ?? null,
    audienceSubjectUrl: null,
    thumbnailURL: item.thumbImgURL || item.imageURL || null,
    fileName: item.path || null,
    mediaURL: item.imageURL || null,
    score: null,
    viralLabel: null,
    ratings: {},
    raw: item,
  };

  if (!respData) return base;

  const analysis = respData.analysis || {};
  const core = analysis.coreAnalysis;

  // v2 shape — detected by the presence of `coreAnalysis` (not by a meta tag).
  if (core && typeof core === 'object') {
    return {
      ...base,
      score: typeof core.overall_score === 'number' ? core.overall_score : null,
      // Viral Potential comes straight from `viral_potential` (the response
      // already sends a display-ready string, e.g. Low/Moderate/High).
      viralLabel: core.viral_potential || null,
      ratings: extractRatings(core.platform_performance),
    };
  }

  // v1 legacy row — degrade gracefully.
  return {
    ...base,
    score: typeof analysis.radScore === 'number' ? analysis.radScore : null,
  };
}

export function parseMediaList(items) {
  return Array.isArray(items) ? items.map(parseMediaItem) : [];
}

/**
 * Resolve each media row's audience name + subjectUrl by joining its
 * `audienceId`/`segmentId` against the /audiences list (state.Audiences.audienceSegments):
 *  - audience matched on `id` → subjectUrl (its `url`, protocol re-added for the link);
 *  - segment matched on `segments[].id` → the audience column name.
 * Rows with no match keep their fallback `name` and a null url. Pure — safe to
 * memoize on (rows, audiences).
 */
export function enrichMediaRows(rows, audiences) {
  if (!Array.isArray(rows)) return [];
  if (!Array.isArray(audiences) || audiences.length === 0) return rows;

  return rows.map((row) => {
    if (row.audienceId == null) return row;
    const audience = audiences.find((a) => String(a.id) === String(row.audienceId));
    if (!audience) return row;

    const segment = (audience.segments || []).find((s) => String(s.id) === String(row.segmentId));
    return {
      ...row,
      name: segment?.name || row.name,
      audienceSubjectUrl: audience.url ? `https://${audience.url}` : row.audienceSubjectUrl,
    };
  });
}

// URL imports send no name, so the backend sets `path` to this placeholder
// (`v2-analyze-YYYYMMDDHHMMSS`). It's skipped by the search — otherwise typing
// `v2`, `analyze` or a date fragment would match every URL import.
export const AUTO_MEDIA_NAME_RE = /^v2-analyze-\d{14}$/;

// A URL plus its decoded copy, so `my photo` matches `.../my%20photo.jpg`.
function urlSearchValues(url) {
  if (!url) return [];
  try {
    return [url, decodeURIComponent(url)];
  } catch (e) {
    return [url];
  }
}

/**
 * Case-insensitive search over the media rows: audience/segment name, audience
 * URL, file name (minus the auto-generated placeholder), media URL and
 * thumbnail URL (raw and decoded). An empty query returns `rows` unchanged.
 */
export function filterMediaRows(rows, query) {
  const q = String(query || '')
    .trim()
    .toLowerCase();
  if (!q) return rows;

  return rows.filter((row) => {
    const fileName = row.fileName && !AUTO_MEDIA_NAME_RE.test(row.fileName) ? row.fileName : null;
    const fields = [
      row.name,
      fileName,
      ...urlSearchValues(row.audienceSubjectUrl),
      ...urlSearchValues(row.mediaURL),
      ...urlSearchValues(row.thumbnailURL),
    ];
    return fields.some((field) =>
      String(field || '')
        .toLowerCase()
        .includes(q)
    );
  });
}
